import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { formatPoints } from "@/lib/format";
import {
    Sparkles, Shield, TrendingUp, Crown, Gem, Lock, CheckCircle2,
    Trophy, Hash, ChevronRight, Plus, Award,
} from "lucide-react";

const LEVEL_ICONS = { novato: Sparkles, amateur: Shield, pro: TrendingUp, elite: Crown, black: Gem };

export default function Levels() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get("/levels")
            .then(({ data }) => setData(data))
            .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo cargar niveles"));
    }, []);

    if (error) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-p2w-gray">{error}</div>;
    if (!data) return <div className="max-w-5xl mx-auto px-4 py-12"><div className="h-96 rounded-2xl bg-p2w-surface border border-p2w-line animate-pulse" /></div>;

    const me = data.me;
    const currentId = me?.current_level;

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-8" data-testid="levels-page">
            {/* HERO */}
            <div className="relative overflow-hidden rounded-3xl border border-p2w-line/80 bg-gradient-to-br from-[#0d0f1a] via-p2w-surface to-[#1a1325] p-6 sm:p-10">
                <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-gradient-to-br from-amber-400/15 to-transparent blur-3xl pointer-events-none" />
                <div className="relative">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-amber-400">
                        <Award className="w-3.5 h-3.5" /> Liga Pool8 LATAM
                    </div>
                    <h1 className="mt-2 font-sora font-extrabold text-3xl sm:text-4xl tracking-tight">
                        Niveles de la <span className="text-amber-400">Liga</span>
                    </h1>
                    <p className="mt-1 text-p2w-gray max-w-2xl">
                        Progresa por habilidad y consistencia. Sube de nivel acumulando puntos por cada predicción y victoria.
                    </p>

                    {/* Rules banner */}
                    <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="rules-banner">
                        <RuleCard icon={Plus} title="+10 pts" subtitle="por cada entrada (predicción)" />
                        <RuleCard icon={Trophy} title="+50 pts" subtitle="por cada victoria" accent="amber" />
                        <RuleCard icon={Crown} title="Top 1% mensual" subtitle="Black automático" accent="magenta" />
                    </div>
                </div>
            </div>

            {/* USER POSITION */}
            {me ? (
                <div className="rounded-3xl border border-p2w-line bg-gradient-to-br from-p2w-surface to-p2w-elevated/40 p-6 sm:p-8" data-testid="my-position-card">
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                        <div className="flex-1">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-p2w-gray font-bold">Tu posición</div>
                            <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                                <CurrentLevelBadge levelId={me.current_level} name={me.current_level_name} />
                                {me.global_rank && (
                                    <span className="inline-flex items-center gap-1.5 text-sm text-p2w-gray font-semibold">
                                        <Hash className="w-3.5 h-3.5" /> Ranking global <span className="text-white">#{me.global_rank}</span>
                                    </span>
                                )}
                            </div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className="font-sora font-extrabold text-4xl text-amber-400 number-tabular" data-testid="my-points">{formatPoints(me.points)}</span>
                                <span className="text-sm text-p2w-gray font-semibold">puntos acumulados</span>
                            </div>

                            {me.next_level ? (
                                <div className="mt-5 max-w-xl">
                                    <div className="flex items-center justify-between text-xs mb-1.5">
                                        <span className="text-p2w-gray">Hacia <b className="text-white">{me.next_level_name}</b></span>
                                        <span className="text-white font-semibold number-tabular">{me.progress_pct}%</span>
                                    </div>
                                    <Progress value={me.progress_pct} className="h-2.5 bg-p2w-bg [&>div]:bg-gradient-to-r [&>div]:from-amber-400 [&>div]:to-amber-200" />
                                    <p className="mt-2 text-sm text-p2w-gray">
                                        Te faltan <span className="text-amber-400 font-bold number-tabular">{formatPoints(me.points_to_next)} pts</span> para subir a {me.next_level_name} <span className="text-p2w-gray/60">({me.progress_pct}% completado)</span>
                                    </p>
                                </div>
                            ) : (
                                <p className="mt-4 text-sm text-p2w-green font-semibold">¡Has alcanzado el nivel máximo! 🎉</p>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[200px]">
                            <Link to="/mercados" className="flex-1"><Button className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold" data-testid="earn-more-btn">Ganar más puntos</Button></Link>
                            <Link to="/rankings" className="flex-1"><Button variant="outline" className="w-full border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line" data-testid="see-ranking-btn">Ver ranking global</Button></Link>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="rounded-3xl border border-p2w-line bg-p2w-surface p-8 text-center">
                    <p className="text-p2w-gray">Inicia sesión para ver tu posición en la liga.</p>
                    <Link to="/login" className="mt-3 inline-block text-p2w-green font-semibold hover:underline">Entrar</Link>
                </div>
            )}

            {/* LEVELS SCALE */}
            <div className="space-y-4" data-testid="levels-scale">
                <div className="flex items-center justify-between">
                    <h2 className="font-sora font-bold text-2xl tracking-tight">Escala de progresión</h2>
                    <span className="text-xs text-p2w-gray">{data.levels.length} niveles</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {data.levels.map((lvl, idx) => {
                        const isCurrent = currentId === lvl.id;
                        const isUnlocked = me ? me.points >= lvl.min : idx === 0;
                        const isNext = me && me.next_level === lvl.id;
                        return (
                            <LevelCard key={lvl.id} lvl={lvl} isCurrent={isCurrent} isUnlocked={isUnlocked} isNext={isNext} />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function RuleCard({ icon: Icon, title, subtitle, accent }) {
    const c = accent === "amber" ? "text-amber-400 border-amber-400/30 bg-amber-400/5"
        : accent === "magenta" ? "text-p2w-magenta border-p2w-magenta/30 bg-p2w-magenta/5"
        : "text-blue-400 border-blue-400/30 bg-blue-400/5";
    return (
        <div className={`rounded-xl border ${c} p-4 backdrop-blur-sm`}>
            <Icon className="w-4 h-4 opacity-80" />
            <div className="mt-2 font-sora font-extrabold text-xl">{title}</div>
            <div className="text-xs text-p2w-gray mt-0.5">{subtitle}</div>
        </div>
    );
}

function CurrentLevelBadge({ levelId, name }) {
    const Icon = LEVEL_ICONS[levelId] || Sparkles;
    const colorMap = {
        novato: "#9CA0AE", amateur: "#3B82F6", pro: "#9FE80D",
        elite: "#F5A623", black: "#D6249F",
    };
    const c = colorMap[levelId] || "#9CA0AE";
    return (
        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full font-bold uppercase tracking-wider text-xs"
            style={{ background: `${c}22`, color: c, border: `1px solid ${c}66` }}>
            <Icon className="w-4 h-4" /> {name}
        </span>
    );
}

function LevelCard({ lvl, isCurrent, isUnlocked, isNext }) {
    const Icon = LEVEL_ICONS[lvl.id] || Sparkles;
    const range = lvl.max ? `${formatPoints(lvl.min)} – ${formatPoints(lvl.max)} pts` : `${formatPoints(lvl.min)}+ pts`;

    return (
        <div
            data-testid={`level-card-${lvl.id}`}
            className={`relative group rounded-2xl border p-5 transition-all duration-300 ${
                isCurrent ? "border-2 shadow-lg" : isUnlocked ? "border-p2w-line bg-p2w-surface" : "border-p2w-line/50 bg-p2w-surface/60"
            } ${!isUnlocked ? "opacity-60" : "hover:-translate-y-0.5 hover:border-p2w-purple/40"}`}
            style={isCurrent ? { borderColor: lvl.color, background: `linear-gradient(135deg, ${lvl.color}1A, transparent 60%)` } : undefined}
        >
            {isCurrent && (
                <span className="absolute -top-2 right-4 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold bg-p2w-green text-p2w-green-fg shadow-glow-green">
                    Actual
                </span>
            )}
            {isNext && !isCurrent && (
                <span className="absolute -top-2 right-4 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold bg-amber-400 text-black">
                    Siguiente
                </span>
            )}
            {!isUnlocked && (
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] text-p2w-gray uppercase font-semibold">
                    <Lock className="w-3 h-3" /> bloqueado
                </span>
            )}
            <div className="flex items-center gap-3">
                <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-105"
                    style={{ background: `${lvl.color}1A`, color: lvl.color, border: `1px solid ${lvl.color}55` }}
                >
                    <Icon className="w-5 h-5" />
                </div>
                <div>
                    <div className="font-sora font-extrabold text-lg tracking-tight" style={{ color: lvl.color }}>{lvl.name}</div>
                    <div className="text-xs text-p2w-gray number-tabular">{range}</div>
                </div>
            </div>
            <ul className="mt-4 space-y-1.5">
                {lvl.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-p2w-gray/90">
                        {isUnlocked
                            ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: lvl.color }} />
                            : <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-p2w-gray/40" />}
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
